/**
 * The drop page, at `/u/:token`. The one screen in this product built for
 * somebody who does not have an account and never will.
 *
 * WHO IS LOOKING AT THIS. A photographer, a videographer, a client's assistant.
 * They were sent a link by the person who owns the app. They do not know what
 * SV Cloud is, they will not read a paragraph, and they have two thousand files
 * and a deadline. Everything below follows from that.
 *
 * THE PAGE NEVER SAYS "RESUMABLE". It says "if your internet drops, drag the
 * same folder in again and it picks up where it stopped", because the word is
 * jargon and the instruction is the only part they need to act on. This is the
 * single most important sentence on the screen: without it, somebody whose
 * upload dies at 80% assumes the whole thing is ruined and starts over, or
 * gives up and emails a WeTransfer link instead.
 *
 * IT NAMES NOTHING ABOUT THE PROJECT. No app name, no owner, no folder path,
 * no web address. Only the label the owner wrote for this specific delivery.
 * A link handed to an outside contractor is not a place to leak what else the
 * account has.
 *
 * THE FILE LIST IS CAPPED AT WHAT IS WORTH LOOKING AT. Two thousand rows is
 * not a progress display, it is a stall; so the rows show what is moving and
 * what went wrong, and everything finished collapses into a count. The overall
 * bar is the real answer to "how long".
 *
 * FAILURES ARE PER FILE AND NEVER FATAL. One refused file does not stop a
 * delivery, and the summary at the end names what did not make it so the
 * person can act rather than guess.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Notice } from '../../components/patterns/Notice';
import { captureEvent } from '../../lib/analytics/posthog';
import { CLOUD_API_URL } from '../../lib/http';
import { bytes as formatBytes, count } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import {
  type DropInfo,
  DropUploader,
  type UploadItem,
  type UploadProgress,
} from '../../lib/upload/client';
import { fetchDropInfo } from '../../lib/upload/client';
import { type Challenge, createChallenge } from '../../lib/upload/turnstile';

import '../../styles/screens/drop.css';

/** Rows shown at once. Beyond this the list is a stall, not a progress display. */
const ROWS_SHOWN = 40;

function relativeDeadline(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return t('expired');
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return t('in under an hour');
  if (hours < 24) return t('in about {n} hours', { n: String(hours) });
  const days = Math.round(hours / 24);
  return days === 1 ? t('tomorrow') : t('in about {n} days', { n: String(days) });
}

function stateWord(item: UploadItem): string {
  switch (item.state) {
    case 'done':
      return t('Sent');
    case 'skipped':
      return t('Already sent');
    case 'refused':
      return t('Not accepted');
    case 'failed':
      return t("Didn't finish");
    case 'uploading':
      return t('Sending');
    default:
      return t('Waiting');
  }
}

export default function Drop() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<DropInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const uploader = useRef<DropUploader | null>(null);
  const challengeBox = useRef<HTMLDivElement | null>(null);
  const challenge = useRef<Challenge | null>(null);
  const siteKey = info?.challenge.site_key ?? null;

  // The bot check (lib/upload/turnstile.ts), rendered once the link says it
  // needs one. Invisible unless Cloudflare asks the visitor to click.
  useEffect(() => {
    if (!siteKey || !challengeBox.current) return;
    let live = true;
    createChallenge(challengeBox.current, siteKey)
      .then((c) => {
        if (live) challenge.current = c;
        else c.dispose();
      })
      .catch(() => {
        // Surfaces at upload time: the token getter below reports it.
      });
    return () => {
      live = false;
      challenge.current?.dispose();
      challenge.current = null;
    };
  }, [siteKey]);
  const filesInput = useRef<HTMLInputElement | null>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const dragDepth = useRef(0);

  useEffect(() => {
    let live = true;
    fetchDropInfo(CLOUD_API_URL, token)
      .then((next) => {
        if (!live) return;
        setInfo(next);
        captureEvent('drop_page_viewed', {
          remaining_bytes: next.limits.remaining_bytes,
          already_uploaded_files: next.uploaded.files,
        });
      })
      .catch((err: Error) => {
        if (!live) return;
        setLoadError(err.message);
        captureEvent('drop_page_unavailable', { reason: err.message });
      });
    return () => {
      live = false;
    };
  }, [token]);

  const start = useCallback(
    async (files: File[]) => {
      if (files.length === 0 || !info) return;
      setFatal(null);
      captureEvent('drop_upload_started', {
        file_count: files.length,
        total_bytes: files.reduce((sum, f) => sum + f.size, 0),
      });
      const engine = new DropUploader(CLOUD_API_URL, token, setProgress);
      if (siteKey) {
        engine.setChallengeTokenSource(async () => {
          if (!challenge.current) {
            throw new Error(t('The security check could not load. Reload the page and try again.'));
          }
          return challenge.current.token();
        });
      }
      uploader.current = engine;
      try {
        const final = await engine.upload(files, info.protocol.batch_max);
        captureEvent('drop_upload_finished', {
          done: final.done,
          skipped: final.skipped,
          failed: final.failed,
          refused: final.refused,
          bytes: final.sentBytes,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : t('The upload could not start.');
        setFatal(message);
        captureEvent('drop_upload_failed', { reason: message });
      }
    },
    [info, token, siteKey],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setOver(false);
      void start(Array.from(event.dataTransfer?.files ?? []));
    },
    [start],
  );

  const summary = useMemo(() => {
    if (!progress) return null;
    const percent =
      progress.totalBytes === 0
        ? 0
        : Math.min(100, (progress.sentBytes / progress.totalBytes) * 100);
    const moving = progress.items.filter(
      (i) => i.state === 'uploading' || i.state === 'waiting',
    );
    const wrong = progress.items.filter((i) => i.state === 'failed' || i.state === 'refused');
    return { percent, moving, wrong };
  }, [progress]);

  if (loadError) {
    return (
      <div className="drop">
        <h1 className="drop__title">{t('This link is not available')}</h1>
        <p className="drop__lede">{loadError}</p>
        <p className="drop__lede">
          {t('Ask the person who sent it to you for a new one. Links stop working after a while, on purpose.')}
        </p>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="drop">
        <p className="drop__lede">{t('Loading…')}</p>
      </div>
    );
  }

  const finished = progress !== null && !progress.running;
  const remaining = info.limits.remaining_bytes;

  return (
    <div className="drop">
      <header className="drop__head">
        <p className="drop__eyebrow">{t('Upload')}</p>
        <h1 className="drop__title">{info.label}</h1>
        <p className="drop__lede">
          {t('Drop your files below. You do not need an account, and there is nothing to install.')}
        </p>
      </header>
      {/* Turnstile mounts here; it shows only if Cloudflare asks for a click. */}
      <div ref={challengeBox} className="drop__challenge" />

      {/* The three facts that decide whether this goes well, stated before
          anything is dragged. The middle one is the one that saves a delivery. */}
      <ul className="drop__facts">
        <li>
          <strong>{t('Photos and video are both fine.')}</strong>{' '}
          {t('Large video is expected. Please do not zip anything, it makes this slower rather than faster.')}
        </li>
        <li>
          <strong>{t('If your internet drops, nothing is lost.')}</strong>{' '}
          {t('Open this same link again and pick the same files. The ones that already arrived are skipped, and a large video carries on from where it stopped.')}
        </li>
        <li>
          <strong>{t('This link stops working {when}.', { when: relativeDeadline(info.expires_at) })}</strong>{' '}
          {t('There is room for {size} in total.', { size: formatBytes(remaining) })}
        </li>
      </ul>

      <div
        className="drop__zone"
        data-over={over ? 'yes' : 'no'}
        onDragEnter={(e) => {
          if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
          e.preventDefault();
          dragDepth.current += 1;
          setOver(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setOver(false);
        }}
        onDrop={onDrop}
      >
        {/* Both lines are rendered and CSS picks one by pointer type. A phone
            cannot drag anything, and telling somebody on an iPhone to "drag
            your folder here" is the page failing at its only job. Done in CSS
            rather than by sniffing the user agent, which gets this wrong on
            exactly the touch laptops where it matters. */}
        <p className="drop__zoneline drop__zoneline--fine">
          {t('Drag your photos or your whole folder here')}
        </p>
        <p className="drop__zoneline drop__zoneline--coarse">
          {t('Choose the photos or videos you want to send')}
        </p>
        <div className="drop__actions">
          <Button variant="solid" onClick={() => filesInput.current?.click()}>
            {t('Choose files')}
          </Button>
          {/* Folder picking is webkitdirectory, which iOS Safari does not
              implement, so offering it there is a button that does nothing.
              Hidden on coarse pointers; "Choose files" still takes a whole
              multi-selection from the photo library. */}
          <span className="drop__folderpick">
            <Button variant="outline" onClick={() => folderInput.current?.click()}>
              {t('Choose a folder')}
            </Button>
          </span>
        </div>
        <input
          ref={filesInput}
          type="file"
          multiple
          hidden
          onChange={(e) => void start(Array.from(e.target.files ?? []))}
        />
        {/* webkitdirectory is not in React's typings but every browser that
            matters implements it, and folder-at-a-time is how a delivery
            actually arrives. */}
        <input
          ref={folderInput}
          type="file"
          multiple
          hidden
          // @ts-expect-error -- non-standard attribute, intentionally set
          webkitdirectory=""
          onChange={(e) => void start(Array.from(e.target.files ?? []))}
        />
      </div>

      {fatal ? (
        <Notice tone="attention" title={t('The upload stopped')}>
          {fatal}
        </Notice>
      ) : null}

      {progress && summary ? (
        <section className="drop__progress" aria-live="polite">
          <div className="drop__bar">
            <div className="drop__bartrack" style={{ width: `${summary.percent}%` }} />
          </div>
          <p className="drop__count">
            {progress.running
              ? t('{done} of {total} sent, {size} so far', {
                  done: count(progress.done + progress.skipped),
                  total: count(progress.items.length),
                  size: formatBytes(progress.sentBytes),
                })
              : t('{done} of {total} files are on their way to the owner.', {
                  done: count(progress.done + progress.skipped),
                  total: count(progress.items.length),
                })}
          </p>

          {summary.moving.length > 0 ? (
            <ul className="drop__rows">
              {summary.moving.slice(0, ROWS_SHOWN).map((item) => (
                <li className="drop__row" key={item.path}>
                  <span className="drop__rowname mono">{item.path}</span>
                  <span className="drop__rowstate">
                    {item.state === 'uploading' && item.bytes > 0
                      ? `${Math.round((item.sent / item.bytes) * 100)}%`
                      : stateWord(item)}
                  </span>
                </li>
              ))}
              {summary.moving.length > ROWS_SHOWN ? (
                <li className="drop__row drop__row--more">
                  {t('and {n} more waiting', { n: count(summary.moving.length - ROWS_SHOWN) })}
                </li>
              ) : null}
            </ul>
          ) : null}

          {finished && summary.wrong.length > 0 ? (
            <>
              <Notice
                tone="attention"
                title={t("{n} files didn't make it", { n: count(summary.wrong.length) })}
              >
                {t('Pick the same files again. Everything that already arrived is skipped, so only these are retried.')}
              </Notice>
              <ul className="drop__rows">
                {summary.wrong.slice(0, ROWS_SHOWN).map((item) => (
                  <li className="drop__row" key={item.path}>
                    <span className="drop__rowname mono">{item.path}</span>
                    <span className="drop__rowstate">{item.message ?? stateWord(item)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {finished && summary.wrong.length === 0 ? (
            <Notice tone="done" title={t('All done')}>
              {t('Everything arrived. You can close this page.')}
            </Notice>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
